import Vue from "vue";
import VueI18n from "vue-i18n";
import en from "./locales/en.js";
import es from "./locales/es.js";
import fr from "./locales/fr.js";
import uk from "./locales/uk.js";
import pt from "./locales/pt.js";
import cronjobsLocaleEn from "./modules/cronjobs_module/locales/en.js";
import cronjobsLocaleEs from "./modules/cronjobs_module/locales/es.js";
import comexstatsLocaleEn from "./modules/comexstats_module/locales/en.js";
import comexstatsLocaleEs from "./modules/comexstats_module/locales/es.js";
import historyLocaleEn from "./modules/history_module/locales/en.js";
import historyLocaleEs from "./modules/history_module/locales/es.js";
import bolsaComercialesLocaleEn from "./modules/bolsa_comerciales_module/locales/en.js";
import bolsaComercialesLocaleEs from "./modules/bolsa_comerciales_module/locales/es.js";
import fobtradingLocaleEn from "./modules/fobtrading_module/locales/en.js";
import fobtradingLocaleEs from "./modules/fobtrading_module/locales/es.js";
import arcaLocaleEn from "./modules/arca_module/locales/en.js";
import arcaLocaleEs from "./modules/arca_module/locales/es.js";
import { SUBDOMAIN_TO_MODULE_FOR_DOMAIN_LOCALES } from "./i18n/domainModuleLocaleRegistry.js";

Vue.use(VueI18n);

/** Traducciones planas por subdominio: cada módulo puede tener locales/domain/es|en.js (webpack las empaqueta). */
const moduleDomainLocaleReq = require.context(
  "./modules",
  true,
  new RegExp("^\\.\\/[^/]+\\/locales\\/domain\\/(es|en)\\.js$")
);

function loadModuleDomainFlat(moduleFolder, localeCode) {
  const key = `./${moduleFolder}/locales/domain/${localeCode}.js`;
  if (!moduleDomainLocaleReq.keys().includes(key)) return null;
  try {
    const mod = moduleDomainLocaleReq(key);
    return (mod && mod.default) || mod || null;
  } catch (e) {
    return null;
  }
}

let lastModuleDomainLocaleApplyKey = "";

function readCachedActiveModulesForDomainLocales() {
  try {
    const raw = localStorage.getItem("activeModules");
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

/**
 * Convierte objeto plano { "a.b.c": "val" } en anidado { a: { b: { c: "val" } } }
 * para merge en vue-i18n.
 */
function flatToNested(flat) {
  const result = {};
  if (!flat || typeof flat !== "object") return result;
  for (const [key, value] of Object.entries(flat)) {
    const parts = String(key).split(".");
    let current = result;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (!current[p] || typeof current[p] !== "object") current[p] = {};
      current = current[p];
    }
    current[parts[parts.length - 1]] = value;
  }
  return result;
}

/** Copia de los mensajes base (archivos .js) antes de mergear overrides. Para mostrar "valor original" en el módulo de traducciones. */
let baseMessagesByLocale = {};

const TRANSLATION_OVERRIDE_LOCALES = ["en", "es", "fr", "uk", "pt"];

function translationOverridesStorageKey() {
  try {
    return `translationOverrides_v1_${window.location.hostname}`;
  } catch (e) {
    return "translationOverrides_v1";
  }
}

function ensureBaseMessagesSnapshot() {
  if (Object.keys(baseMessagesByLocale).length === 0 && i18n && i18n.messages) {
    for (const locale of TRANSLATION_OVERRIDE_LOCALES) {
      if (i18n.messages[locale]) {
        baseMessagesByLocale[locale] = JSON.parse(JSON.stringify(i18n.messages[locale]));
      }
    }
  }
}

/**
 * Aplica overrides guardados en localStorage (por hostname). Sin red.
 * Si el JSON es inválido o no tiene forma esperada, borra la entrada para que el bootstrap vuelva a pedir al API.
 */
export function applyTranslationOverridesFromLocalStorage() {
  try {
    const raw = localStorage.getItem(translationOverridesStorageKey());
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const byLocale = parsed && parsed.byLocale ? parsed.byLocale : null;
    if (!byLocale || typeof byLocale !== "object") {
      clearTranslationOverridesCache();
      return;
    }
    ensureBaseMessagesSnapshot();
    for (const locale of TRANSLATION_OVERRIDE_LOCALES) {
      const data = byLocale[locale];
      if (!data || typeof data !== "object" || Object.keys(data).length === 0) continue;
      const nested = flatToNested(data);
      if (Object.keys(nested).length > 0) {
        i18n.mergeLocaleMessage(locale, nested);
      }
    }
  } catch (e) {
    clearTranslationOverridesCache();
  }
}

export function hasTranslationOverridesCache() {
  try {
    return !!localStorage.getItem(translationOverridesStorageKey());
  } catch (e) {
    return false;
  }
}

export function clearTranslationOverridesCache() {
  try {
    localStorage.removeItem(translationOverridesStorageKey());
  } catch (e) {
    // ignore
  }
}

/**
 * Pide overrides al API, aplica a i18n y persiste en localStorage (por hostname).
 * Llamar tras login exitoso o al guardar en el módulo de traducciones.
 */
export async function fetchTranslationOverridesFromApiAndPersist() {
  ensureBaseMessagesSnapshot();
  const { wencesApi } = await import("@/api/wencesApi");
  const byLocale = {};
  for (const locale of TRANSLATION_OVERRIDE_LOCALES) {
    try {
      const res = await wencesApi.get("api/translation-overrides", {
        params: { locale },
      });
      const data = res.data?.data && typeof res.data.data === "object" ? res.data.data : {};
      byLocale[locale] = data;
      if (Object.keys(data).length === 0) continue;
      const nested = flatToNested(data);
      if (Object.keys(nested).length > 0) {
        i18n.mergeLocaleMessage(locale, nested);
      }
    } catch (e) {
      byLocale[locale] = {};
    }
  }
  try {
    localStorage.setItem(
      translationOverridesStorageKey(),
      JSON.stringify({ byLocale })
    );
  } catch (e) {
    // quota exceeded u offline
  }
}

/** Misma lógica que fetchTranslationOverridesFromApiAndPersist (p. ej. tras guardar en Traducciones). */
export async function loadTranslationOverrides() {
  await fetchTranslationOverridesFromApiAndPersist();
}

// Lista de idiomas disponibles en el sistema
const availableLanguages = ["en", "es", "fr", "uk", "pt"];

function hasSessionTokenInStorage() {
  try {
    const token = localStorage.getItem("token");
    return !!(token && String(token).length >= 10);
  } catch (e) {
    return false;
  }
}

/**
 * Idioma inicial de vue-i18n antes del primer route.
 * Sin sesión: siempre español (pantallas públicas / anónimas).
 * Con sesión: preferencia guardada o español.
 */
function getInitialLocale() {
  try {
    if (!hasSessionTokenInStorage()) {
      return "es";
    }
    const s = localStorage.getItem("language");
    const t = s && String(s).trim().toLowerCase();
    if (t && availableLanguages.includes(t)) return t;
    return "es";
  } catch (e) {
    return "es";
  }
}

const savedLanguage = getInitialLocale();

// Función para detectar el dominio y cargar traducciones específicas
function getDomainSpecificTranslations() {
  const hostname = window.location.hostname;
  let domainTranslations = {};

  // Detectar si es un subdominio de delegat.es o delegat.test
  if (hostname.includes("delegat.es") || hostname.includes("delegat.test") || hostname.includes("iajur.com")) {
    const subdomain = hostname.split(".")[0];

    // Cargar traducciones específicas del dominio si existen
    try {
      if (subdomain) {
        let domainEs = {};
        let domainEn = {};
        if (!SUBDOMAIN_TO_MODULE_FOR_DOMAIN_LOCALES[subdomain]) {
          try {
            domainEs = require(`./locales/${subdomain}/es.js`).default;
          } catch (esError) {}
          try {
            domainEn = require(`./locales/${subdomain}/en.js`).default;
          } catch (enError) {}
        }

        // Solo crear domainTranslations si hay al menos una traducción específica
        if (
          Object.keys(domainEs).length > 0 ||
          Object.keys(domainEn).length > 0
        ) {
          domainTranslations = {
            es: { ...es, ...domainEs },
            en: { ...en, ...domainEn },
          };
        } else {
        }
      }
    } catch (error) {
      console.warn(
        `⚠️ Could not load domain-specific translations for ${subdomain}:`,
        error
      );
    }
  }

  return domainTranslations;
}

// Obtener traducciones específicas del dominio
const domainTranslations = getDomainSpecificTranslations();

// Traducciones del módulo CRONJOBS (encapsuladas en modules/cronjobs_module/locales)
const cronjobsEn = cronjobsLocaleEn.default || cronjobsLocaleEn;
const cronjobsEs = cronjobsLocaleEs.default || cronjobsLocaleEs;
const comexstatsEn = comexstatsLocaleEn.default || comexstatsLocaleEn;
const comexstatsEs = comexstatsLocaleEs.default || comexstatsLocaleEs;
const historyEn = historyLocaleEn.default || historyLocaleEn;
const historyEs = historyLocaleEs.default || historyLocaleEs;
const bolsaComercialesEn = bolsaComercialesLocaleEn.default || bolsaComercialesLocaleEn;
const bolsaComercialesEs = bolsaComercialesLocaleEs.default || bolsaComercialesLocaleEs;
const fobtradingEn = fobtradingLocaleEn.default || fobtradingLocaleEn;
const fobtradingEs = fobtradingLocaleEs.default || fobtradingLocaleEs;
const arcaEn = arcaLocaleEn.default || arcaLocaleEn;
const arcaEs = arcaLocaleEs.default || arcaLocaleEs;

// Configuración de VueI18n (los overrides se aplican después vía loadTranslationOverrides)
export const i18n = new VueI18n({
  locale: savedLanguage, // Usar el idioma detectado o guardado
  messages: {
    en: {
      ...(domainTranslations.en || en),
      ...cronjobsEn,
      ...comexstatsEn,
      ...historyEn,
      ...bolsaComercialesEn,
      ...fobtradingEn,
      ...arcaEn,
    },
    es: {
      ...(domainTranslations.es || es),
      ...cronjobsEs,
      ...comexstatsEs,
      ...historyEs,
      ...bolsaComercialesEs,
      ...fobtradingEs,
      ...arcaEs,
    },
    fr: {
      ...fr,
      cronjobs: cronjobsEn.cronjobs,
      comexstats: comexstatsEn.comexstats,
      history: historyEn.history,
      bolsaComerciales: bolsaComercialesEn.bolsaComerciales,
      fobtrading: fobtradingEn.fobtrading,
      arca: arcaEn.arca,
    },
    uk: {
      ...uk,
      cronjobs: cronjobsEn.cronjobs,
      comexstats: comexstatsEn.comexstats,
      history: historyEn.history,
      bolsaComerciales: bolsaComercialesEn.bolsaComerciales,
      fobtrading: fobtradingEn.fobtrading,
      arca: arcaEn.arca,
    },
    pt: {
      ...pt,
      cronjobs: cronjobsEn.cronjobs,
      comexstats: comexstatsEn.comexstats,
      history: historyEn.history,
      bolsaComerciales: bolsaComercialesEn.bolsaComerciales,
      fobtrading: fobtradingEn.fobtrading,
      arca: arcaEn.arca,
    },
  },
});

/**
 * Overlays de etiquetas por subdominio (claves en raíz de vue-i18n), solo si el módulo
 * asociado en domainModuleLocaleRegistry está en activeModules.
 * Idempotente: misma firma (subdominio + lista de módulos activos) no vuelve a mergear.
 */
export function applyModuleDomainLocaleOverlays(activeModules) {
  if (!i18n || typeof i18n.mergeLocaleMessage !== "function") return;
  let hostname = "";
  try {
    hostname = window.location.hostname;
  } catch (e) {
    return;
  }
  const subdomain = hostname.split(".")[0];
  const moduleName = SUBDOMAIN_TO_MODULE_FOR_DOMAIN_LOCALES[subdomain];
  if (!moduleName) return;

  const names = Array.isArray(activeModules)
    ? activeModules.map((m) => (m && m.name ? String(m.name) : "")).filter(Boolean)
    : [];
  const applyKey = `${subdomain}:${[...names].sort().join(",")}`;
  if (applyKey === lastModuleDomainLocaleApplyKey) return;

  lastModuleDomainLocaleApplyKey = applyKey;

  if (!names.includes(moduleName)) return;

  const flatEs = loadModuleDomainFlat(moduleName, "es");
  const flatEn = loadModuleDomainFlat(moduleName, "en");
  if (flatEs && typeof flatEs === "object" && Object.keys(flatEs).length > 0) {
    i18n.mergeLocaleMessage("es", flatEs);
  }
  if (flatEn && typeof flatEn === "object" && Object.keys(flatEn).length > 0) {
    i18n.mergeLocaleMessage("en", flatEn);
  }
}

applyModuleDomainLocaleOverlays(readCachedActiveModulesForDomainLocales());

/**
 * Sin token de sesión: UI y preferencia de idioma para cabeceras API en español.
 * Con sesión no hace nada (el perfil / syncI18nLocaleFromUser gobierna el idioma).
 */
export function applyAnonymousSessionLocale() {
  if (!i18n) return;
  try {
    if (hasSessionTokenInStorage()) return;
    if (i18n.locale !== "es") {
      i18n.locale = "es";
    }
    localStorage.setItem("language", "es");
    document.documentElement.lang = "es";
  } catch (e) {
    // ignore
  }
}

applyAnonymousSessionLocale();

/*
 * Dos idiomas por usuario:
 * - El de la app (`business_language` en el usuario): rige `i18n.locale`, o sea todo
 *   `$t()`: menú, botones, filtros, campos, columnas. Vacío = el de avisos.
 * - El de avisos y soporte (`language`): alertas, notificaciones, validaciones y la
 *   sección de soporte, que se traducen con `$tn()`. Sin elegir = español.
 * En localStorage, "language" guarda el de la app (la cabecera `savedLanguage` que lee
 * el back sale de ahí) y "notice_language" el de avisos.
 */
const NOTICE_LANGUAGE_STORAGE_KEY = "notice_language";
const DEFAULT_NOTICE_LANGUAGE = "es";

function normalizeLanguage(value) {
  const t = typeof value === "string" ? value.trim().toLowerCase() : "";
  return availableLanguages.includes(t) ? t : "";
}

function readStoredNoticeLanguage() {
  try {
    return normalizeLanguage(localStorage.getItem(NOTICE_LANGUAGE_STORAGE_KEY)) || DEFAULT_NOTICE_LANGUAGE;
  } catch (e) {
    return DEFAULT_NOTICE_LANGUAGE;
  }
}

const noticeLanguageState = Vue.observable({ code: readStoredNoticeLanguage() });

function setNoticeLanguage(code) {
  noticeLanguageState.code = code;
  try {
    localStorage.setItem(NOTICE_LANGUAGE_STORAGE_KEY, code);
  } catch (e) {
    // ignore
  }
}

/** Idioma de avisos y soporte. Reactivo dentro de un render o computed. */
export function getNoticeLocale() {
  return noticeLanguageState.code;
}

/** Como `$t`, pero en el idioma de avisos y soporte. */
export function tn(key, values) {
  if (key === undefined || key === null || key === "") return "";
  return i18n.t(String(key), getNoticeLocale(), values);
}

Vue.prototype.$tn = function $tn(key, values) {
  return tn(key, values);
};

Object.defineProperty(Vue.prototype, "$noticeLocale", {
  get() {
    return getNoticeLocale();
  },
  configurable: true,
});

/**
 * Tras login o check-status: alinea vue-i18n, localStorage y document.documentElement.lang
 * con los idiomas del perfil. Sin usuario, usa localStorage si es un código válido.
 */
export function syncI18nLocaleFromUser(user) {
  if (!i18n) return;
  let candidate = "";
  try {
    if (user && typeof user === "object") {
      const notice = normalizeLanguage(user.language) || DEFAULT_NOTICE_LANGUAGE;
      setNoticeLanguage(notice);
      candidate = normalizeLanguage(user.business_language) || notice;
    } else {
      candidate = normalizeLanguage(localStorage.getItem("language"));
    }
  } catch (e) {
    return;
  }
  if (!candidate) return;
  if (i18n.locale !== candidate) {
    i18n.locale = candidate;
  }
  try {
    localStorage.setItem("language", candidate);
    document.documentElement.lang = candidate;
  } catch (e) {
    // ignore
  }
}

/**
 * Aplana los mensajes anidados de un locale a lista de { key, value }.
 * Sirve para que el módulo de traducciones muestre qué claves existen (archivos .js + overrides).
 */
function flattenMessages(obj, prefix = "") {
  if (!obj || typeof obj !== "object") return [];
  const result = [];
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      result.push(...flattenMessages(v, path));
    } else {
      const value = typeof v === "string" ? v : (v == null ? "" : String(v));
      result.push({ key: path, value });
    }
  }
  return result;
}

/**
 * Devuelve todas las claves de traducción existentes para un locale (base + overrides ya mergeados).
 */
export function getExistingTranslationKeys(locale) {
  if (!i18n || !i18n.messages || !i18n.messages[locale]) return [];
  return flattenMessages(i18n.messages[locale], "").sort((a, b) =>
    a.key.localeCompare(b.key)
  );
}

/**
 * Devuelve las claves del locale tal como están en los archivos .js (sin overrides).
 * Para mostrar el "valor original" en el listado de traducciones.
 */
export function getBaseTranslationKeys(locale) {
  const base = baseMessagesByLocale[locale] || (i18n && i18n.messages && i18n.messages[locale]) || {};
  return flattenMessages(base, "").sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * Devuelve la unión de todas las claves que existen en algún locale (archivos + overrides ya mergeados).
 * Sirve para mostrar en el listado claves que existen en en.js pero no en es.js, y poder añadir la traducción.
 */
export function getAllKeysFromAllLocales() {
  const locales = ["en", "es", "fr", "uk", "pt"];
  const keySet = new Set();
  for (const locale of locales) {
    const keys = getExistingTranslationKeys(locale);
    keys.forEach((item) => keySet.add(item.key));
  }
  return Array.from(keySet).sort((a, b) => a.localeCompare(b));
}

export default i18n;
